// Alert-ladder editor. Requirement 5.2.
//
// A controlled component over a list of AlertLevelInput rows. Supports add / edit /
// disable / delete / reorder / restore-default, surfaces client-side validation that
// mirrors the backend rules (Requirement 6), and renders a preview of trigger prices
// against the current all-time high.

import { useMemo, type ReactNode } from "react";
import { Trash2 } from "lucide-react";

import type { AlertLevelInput } from "../types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

const DEFAULT_SP500_LADDER: AlertLevelInput[] = [
  { drawdown_percent: 10, maximum_margin_percent: 2, enabled: true, conditional: false, label: "Initial correction" },
  { drawdown_percent: 15, maximum_margin_percent: 5, enabled: true, conditional: false, label: "Moderate correction" },
  { drawdown_percent: 20, maximum_margin_percent: 7.5, enabled: true, conditional: false, label: "Bear market" },
  { drawdown_percent: 25, maximum_margin_percent: 10, enabled: true, conditional: false, label: "Deep correction" },
  { drawdown_percent: 30, maximum_margin_percent: 15, enabled: true, conditional: true, label: "Severe bear market" },
  { drawdown_percent: 35, maximum_margin_percent: 20, enabled: true, conditional: true, label: "Conditional maximum" },
];

export interface LadderValidation {
  errors: string[];
  warnings: string[];
  canActivate: boolean;
}

// System-wide hard cap on any configured margin ceiling (mirrors the backend's
// SYSTEM_MAX_MARGIN_CEILING). Client-side validation is for UX; the backend is
// authoritative.
export const SYSTEM_MAX_MARGIN_CEILING = 50;

export function validateLadder(
  levels: AlertLevelInput[],
  ordinaryCeiling = 10,
  absoluteCeiling = SYSTEM_MAX_MARGIN_CEILING,
): LadderValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const enabled = levels.filter((l) => l.enabled);

  if (enabled.length === 0) {
    errors.push("At least one enabled alert level is required.");
    return { errors, warnings, canActivate: false };
  }

  for (const l of enabled) {
    if (!(l.drawdown_percent > 0 && l.drawdown_percent < 100)) {
      errors.push(`Drawdown ${l.drawdown_percent}% must be greater than 0 and less than 100.`);
    }
    if (l.maximum_margin_percent < 0) {
      errors.push(`Maximum margin for "${l.label}" cannot be negative.`);
    }
    if (l.maximum_margin_percent > SYSTEM_MAX_MARGIN_CEILING) {
      errors.push(`"${l.label}" exceeds the system maximum of ${SYSTEM_MAX_MARGIN_CEILING}%.`);
    }
    if (l.maximum_margin_percent > absoluteCeiling) {
      errors.push(`"${l.label}" exceeds the absolute ceiling of ${absoluteCeiling}%.`);
    }
    if (l.maximum_margin_percent > ordinaryCeiling && !l.conditional) {
      errors.push(`"${l.label}" exceeds the ordinary ceiling of ${ordinaryCeiling}% and must be marked conditional.`);
    }
  }

  const drawdowns = enabled.map((l) => l.drawdown_percent);
  if (new Set(drawdowns).size !== drawdowns.length) {
    errors.push("Drawdown percentages must be unique.");
  }
  const byDepth = [...enabled].sort((a, b) => a.drawdown_percent - b.drawdown_percent);
  for (let i = 1; i < byDepth.length; i++) {
    if (byDepth[i].maximum_margin_percent < byDepth[i - 1].maximum_margin_percent) {
      errors.push("Maximum margin percentages must not decrease as the drawdown deepens.");
      break;
    }
  }
  // Ordering check against display order.
  for (let i = 1; i < enabled.length; i++) {
    if (enabled[i].drawdown_percent < enabled[i - 1].drawdown_percent) {
      errors.push("Alert levels must be ordered from shallowest to deepest drawdown.");
      break;
    }
  }

  return { errors, warnings, canActivate: errors.length === 0 };
}

interface Props {
  levels: AlertLevelInput[];
  onChange: (levels: AlertLevelInput[]) => void;
  allTimeHigh?: number;
  editable?: boolean;
  ordinaryCeiling?: number;
  absoluteCeiling?: number;
  footerAction?: ReactNode;
}

export function LadderEditor({
  levels,
  onChange,
  allTimeHigh,
  editable = true,
  ordinaryCeiling = 10,
  absoluteCeiling = SYSTEM_MAX_MARGIN_CEILING,
  footerAction,
}: Props) {
  const validation = useMemo(
    () => validateLadder(levels, ordinaryCeiling, absoluteCeiling),
    [levels, ordinaryCeiling, absoluteCeiling],
  );

  const update = (index: number, patch: Partial<AlertLevelInput>) => {
    onChange(levels.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const addLevel = () => {
    const deepest = [...levels]
      .filter((level) => level.enabled)
      .sort((a, b) => b.drawdown_percent - a.drawdown_percent)[0];
    const drawdown = Math.min(99.9, (deepest?.drawdown_percent ?? 0) + 5);
    onChange([
      ...levels,
      {
        drawdown_percent: drawdown,
        maximum_margin_percent: deepest?.maximum_margin_percent ?? 0,
        enabled: true,
        conditional: false,
        label: `${drawdown}% drawdown`,
      },
    ]);
  };

  const removeLevel = (index: number) => {
    onChange(levels.filter((_, i) => i !== index));
  };

  const sortByDrawdown = () =>
    onChange(
      [...levels]
        .sort((a, b) => a.drawdown_percent - b.drawdown_percent)
        .map((level, index) => ({ ...level, display_order: index })),
    );

  const restoreDefault = () => onChange(DEFAULT_SP500_LADDER.map((l) => ({ ...l })));

  const triggerPrice = (drawdown: number): string =>
    allTimeHigh === undefined
      ? "—"
      : (allTimeHigh * (1 - drawdown / 100)).toFixed(2);

  const minimumMarginAt = (index: number): number => {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (levels[i].enabled) return Math.max(0, levels[i].maximum_margin_percent);
    }
    return 0;
  };

  if (!editable) {
    if (levels.length === 0) {
      return <p className="text-sm text-muted-foreground">No alert levels configured.</p>;
    }

    return (
      <div className="divide-y divide-border rounded-md border border-border">
        {levels.map((level, i) => (
          <div
            key={level.id ?? `summary-${i}`}
            className={`grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-3 py-2.5 sm:grid-cols-[minmax(8rem,1.4fr)_repeat(2,minmax(6rem,1fr))] ${
              level.enabled ? "" : "opacity-55"
            }`}
            data-testid="ladder-row"
          >
            <div className="col-span-2 min-w-0 sm:col-span-1">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="truncate text-sm font-medium">
                  {level.drawdown_percent}% drawdown
                </span>
                {level.conditional && <Badge variant="outline">Conditional</Badge>}
                {!level.enabled && <Badge variant="secondary">Off</Badge>}
              </div>
            </div>
            <div className="text-sm tabular-nums">
              <span className="block text-[11px] text-muted-foreground">Trigger</span>
              <span data-testid={`trigger-${i}`}>{triggerPrice(level.drawdown_percent)}</span>
            </div>
            <div className="text-right text-sm tabular-nums sm:text-left">
              <span className="block text-[11px] text-muted-foreground">Max margin</span>
              {level.maximum_margin_percent}%
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border">
        <Table className="block sm:table">
          <TableHeader className="hidden sm:table-header-group">
            <TableRow>
              <TableHead>Drawdown %</TableHead>
              <TableHead>Max margin %</TableHead>
              <TableHead className="text-center">Conditional</TableHead>
              <TableHead className="text-center">Enabled</TableHead>
              <TableHead>Trigger price</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody className="block p-0 sm:table-row-group">
            {levels.map((level, i) => (
              <TableRow key={level.id ?? `row-${i}`} data-testid="ladder-row" className="grid grid-cols-4 gap-2 border-x-0 border-t-0 p-3 last:border-b-0 sm:table-row sm:p-0">
                <TableCell className="col-span-2 block p-0 sm:table-cell sm:p-2">
                  <span className="mb-0.5 block text-[11px] text-muted-foreground sm:hidden">Drawdown %</span>
                  <Input
                    type="number"
                    min="0.1"
                    max="99.9"
                    step="0.1"
                    className="w-full sm:w-20 lg:w-24"
                    aria-label={`drawdown-${i}`}
                    aria-invalid={
                      level.enabled &&
                      (!(level.drawdown_percent > 0 && level.drawdown_percent < 100) ||
                        levels.some(
                          (other, otherIndex) =>
                            otherIndex !== i &&
                            other.enabled &&
                            other.drawdown_percent === level.drawdown_percent,
                        ))
                    }
                    value={level.drawdown_percent}
                    onChange={(e) => {
                      const drawdown = Number(e.target.value);
                      update(i, {
                        drawdown_percent: drawdown,
                        label: `${drawdown}% drawdown`,
                      });
                    }}
                    onBlur={sortByDrawdown}
                  />
                </TableCell>
                <TableCell className="col-span-2 block p-0 sm:table-cell sm:p-2">
                  <span className="mb-0.5 block text-[11px] text-muted-foreground sm:hidden">Max margin %</span>
                  <Input
                    type="number"
                    min={minimumMarginAt(i)}
                    max={Math.min(absoluteCeiling, SYSTEM_MAX_MARGIN_CEILING)}
                    step="0.1"
                    className="w-full sm:w-20 lg:w-24"
                    aria-label={`margin-${i}`}
                    aria-invalid={
                      level.enabled &&
                      (level.maximum_margin_percent < minimumMarginAt(i) ||
                        level.maximum_margin_percent > absoluteCeiling ||
                        level.maximum_margin_percent > SYSTEM_MAX_MARGIN_CEILING ||
                        (level.maximum_margin_percent > ordinaryCeiling &&
                          !level.conditional))
                    }
                    value={level.maximum_margin_percent}
                    onChange={(e) => update(i, { maximum_margin_percent: Number(e.target.value) })}
                  />
                </TableCell>
                <TableCell className="flex flex-col items-start gap-1 p-0 sm:table-cell sm:p-2 sm:text-center">
                  <span className="text-[11px] text-muted-foreground sm:hidden">Conditional</span>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    aria-label={`conditional-${i}`}
                    checked={level.conditional}
                    onChange={(e) => update(i, { conditional: e.target.checked })}
                  />
                </TableCell>
                <TableCell className="flex flex-col items-start gap-1 p-0 sm:table-cell sm:p-2 sm:text-center">
                  <span className="text-[11px] text-muted-foreground sm:hidden">Enabled</span>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    aria-label={`enabled-${i}`}
                    checked={level.enabled}
                    onChange={(e) => update(i, { enabled: e.target.checked })}
                  />
                </TableCell>
                <TableCell className="flex flex-col items-start gap-0.5 p-0 tabular-nums sm:table-cell sm:p-2">
                  <span className="text-[11px] text-muted-foreground sm:hidden">Trigger</span>
                  <span data-testid={`trigger-${i}`}>{triggerPrice(level.drawdown_percent)}</span>
                </TableCell>
                <TableCell className="flex items-end justify-end p-0 sm:table-cell sm:p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-destructive"
                    aria-label={`delete-${i}`}
                    onClick={() => removeLevel(i)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={addLevel}>
          Add level
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={restoreDefault}>
          Restore defaults
        </Button>
        {footerAction && <div className="ml-auto">{footerAction}</div>}
      </div>

      {validation.errors.length > 0 && (
        <Alert variant="destructive" data-testid="ladder-errors">
          <AlertTitle>Fix these before activating</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {validation.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {validation.warnings.length > 0 && (
        <Alert data-testid="ladder-warnings">
          <AlertTitle>Warnings</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {validation.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
